import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X, Clock, MapPinned } from "lucide-react";
import { Event } from "@/types/api";
import { formatEventDate, formatEventTime } from "@/lib/time_utils";
import AddToCalendar from "@/components/AddToCalendar";
import EventLink from "@/components/EventLink";
import ShareButton from "@/components/ShareButton";
import VerifiedEventCheck from "@/components/VerifiedEventCheck";
import StandaloneEventPage from "@/components/StandaloneEventPage";
import { ProfileResponse } from "~/functions/api/get-profile/[[username]]";

interface EventPageProps {
    isStandalone?: boolean;
}

// Component for the event page or modal.
const EventPage = ({ isStandalone = false }: EventPageProps) => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // State for creator profile data
    const [creatorProfileData, setCreatorProfileData] = useState<ProfileResponse | null>(null);
    const [profileLoading, setProfileLoading] = useState<boolean>(false);
    const [profileError, setProfileError] = useState<string | null>(null); // For debugging or more specific error handling if needed

    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;

        const fetchEvent = async () => {
            try {
                setLoading(true);
                setError(null);
                setEvent(null);
                setCreatorProfileData(null);
                setProfileLoading(false);
                setProfileError(null);

                if (!eventId) {
                    setError("No event ID provided.");
                    setLoading(false);
                    return;
                }

                const response = await fetch(`/api/get-event/${eventId}`, { signal });

                if (!response.ok) {
                    if (response.status === 404) {
                        setError('Event not found');
                        setEvent(null);
                    } else {
                        throw new Error(`Failed to fetch event (status: ${response.status})`);
                    }
                } else {
                    const data = await response.json() as { event: Event };
                    setEvent(data.event);
                }
            } catch (err) {
                if (err instanceof Error && err.name === 'AbortError') {
                    console.log('Fetch aborted');
                } else {
                    setError(err instanceof Error ? err.message : 'An unknown error occurred');
                }
            } finally {
                if (!signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchEvent();

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isStandalone) {
                handleClose();
            }
        };

        window.addEventListener('keydown', handleEscape);

        return () => {
            window.removeEventListener('keydown', handleEscape);
            abortController.abort();
        };
    }, [eventId, isStandalone]);

    // useEffect for fetching creator profile when in standalone mode and event is loaded
    useEffect(() => {
        // Reset profile states if event or isStandalone changes, to avoid stale data
        setCreatorProfileData(null);
        setProfileLoading(false);
        setProfileError(null);

        if (isStandalone && event && event.created_by) {
            const abortController = new AbortController();
            const signal = abortController.signal;

            const fetchCreatorProfile = async () => {
                setProfileLoading(true);
                try {
                    const response = await fetch(`/api/get-profile-by-id/${event.created_by}`, { signal });
                    if (!response.ok) {
                        if (response.status === 404) {
                            setProfileError('Creator profile not found.');
                        } else {
                            throw new Error(`Failed to fetch creator profile (status: ${response.status})`);
                        }
                        setCreatorProfileData(null); // Ensure data is null on error
                    } else {
                        const data = await response.json() as ProfileResponse;
                        setCreatorProfileData(data);
                    }
                } catch (err) {
                    if (err instanceof Error && err.name === 'AbortError') {
                        console.log('Profile fetch aborted');
                    } else {
                        setProfileError(err instanceof Error ? err.message : 'An unknown error occurred while fetching profile');
                        setCreatorProfileData(null); // Ensure data is null on error
                    }
                } finally {
                    if (!signal.aborted) {
                        setProfileLoading(false);
                    }
                }
            };

            fetchCreatorProfile();

            return () => {
                abortController.abort();
            };
        } else if (isStandalone && event && !event.created_by) {
            // Event is loaded, but no created_by ID. No profile to fetch.
            setProfileLoading(false); // Ensure loading is false
        }
    }, [event, isStandalone]); // Dependencies for fetching profile

    const handleClose = () => {
        navigate(-1);
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !isStandalone) {
            handleClose();
        }
    };

    const formattedStartDate = event ? formatEventDate(event.start_datetime) : undefined;
    const formattedStartTime = event ? formatEventTime(event.start_datetime) : undefined;
    const formattedEndTime = event?.end_datetime ? formatEventTime(event.end_datetime) : undefined;

    const overlayClasses = `fixed inset-0 bg-black bg-opacity-50 z-50 backdrop-blur-sm`;
    const modalClasses = `relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-auto my-8 overflow-hidden`;

    if (loading) {
        if (isStandalone) {
            return (
                <div className="min-h-screen bg-gradient-to-b from-indigo-100 to-purple-50 p-8 flex justify-center items-start">
                    <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-2xl w-full animate-pulse">
                        <div className="h-8 bg-gray-200 rounded mb-4"></div>
                        <div className="h-64 bg-gray-200 rounded mb-4"></div>
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className={overlayClasses}> {/* This div provides the blurred background */}
                    <div
                        className="flex items-center justify-center min-h-screen p-4"
                        onClick={handleOverlayClick} // onClick moved here
                    >
                        <div className={`${modalClasses} p-6 animate-pulse`}>
                            <div className="h-8 bg-gray-200 rounded mb-4"></div>
                            <div className="h-64 bg-gray-200 rounded mb-4"></div>
                            <div className="h-4 bg-gray-200 rounded mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                </div>
            );
        }
    }

    if (error) {
        if (isStandalone) {
            return (
                <div className="min-h-screen bg-gradient-to-b from-indigo-100 to-purple-50 p-8 flex justify-center items-start">
                    <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-2xl w-full">
                        <div className="text-red-500 text-center">
                            <h2 className="text-2xl font-bold mb-2">Error</h2>
                            <p>{error}</p>
                            <button
                                onClick={() => navigate(-1)}
                                className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                            >
                                Go Back
                            </button>
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className={overlayClasses}> {/* This div provides the blurred background */}
                    <div
                        className="flex items-center justify-center min-h-screen p-4"
                        onClick={handleOverlayClick}
                    >
                        <div className={modalClasses}>
                            <div className="p-6 text-red-500 text-center">
                                <h2 className="text-2xl font-bold mb-2">Error</h2>
                                <p>{error}</p>
                                <button
                                    onClick={handleClose}
                                    className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    }

    if (!event) {
        if (isStandalone) {
            return (
                <div className="min-h-screen bg-gradient-to-b from-indigo-100 to-purple-50 p-8 flex justify-center items-start">
                    <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-2xl w-full">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold mb-2">Event Not Found</h2>
                            <button
                                onClick={() => navigate('/')}
                                className="mt-4 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                            >
                                Go to Home
                            </button>
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className={overlayClasses}> {/* This div provides the blurred background */}
                    <div
                        className="flex items-center justify-center min-h-screen p-4"
                        onClick={handleOverlayClick} // onClick moved here
                    >
                        <div className={modalClasses}>
                            <div className="p-6 text-center">
                                <h2 className="text-2xl font-bold mb-2">Event Not Found</h2>
                                <button
                                    onClick={handleClose}
                                    className="mt-4 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    }

    // Main event content rendering
    if (isStandalone) {
        return (
            <StandaloneEventPage
                event={event}
                formattedStartDate={formattedStartDate}
                formattedStartTime={formattedStartTime}
                formattedEndTime={formattedEndTime}
                creatorProfileData={creatorProfileData} />
        );
    } else { // Modal view
        return (
            <div className={overlayClasses}> {/* This div provides the blurred background */}
                <div
                    className="flex items-center justify-center min-h-screen p-4"
                    onClick={handleOverlayClick} // onClick moved here to the centering div
                >
                    {/* Clicks on modalClasses div will not trigger handleOverlayClick due to e.target !== e.currentTarget */}
                    <div className={modalClasses}>
                        <button
                            onClick={handleClose}
                            className="absolute top-4 right-4 z-50 bg-white/80 hover:bg-white text-gray-700 hover:text-red-500 rounded-full p-2 transition-colors"
                            aria-label="Close"
                        >
                            <X className="h-6 w-6" />
                        </button>



                        <div className="p-6 md:p-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-4">{event.title}{event.is_verified && (
                                <VerifiedEventCheck className="inline-flex align-baseline ml-2" />
                            )}</h1>

                            <div className="flex flex-wrap gap-4 mb-6">
                                <div className="bg-gradient-to-br from-fuchsia-500 to-violet-600 rounded-xl px-4 py-2 text-white shadow-lg shadow-fuchsia-200">
                                    <div className="text-2xl font-bold flex items-center justify-center gap-1.5">
                                        <span className="text-fuchsia-100">{formattedStartDate?.month}</span>
                                        <span>{formattedStartDate?.day}</span>
                                    </div>
                                    <div className="h-px bg-white/20 my-1.5 w-full"></div>
                                    <div className="text-sm text-fuchsia-100">
                                        {formattedStartDate?.weekday}
                                    </div>
                                </div>
                                <div className="flex items-center bg-indigo-50 text-indigo-800 rounded-lg px-4 py-2">
                                    <Clock className="h-5 w-5 mr-2 text-indigo-500" />
                                    <span>
                                        {formattedStartTime}
                                        {formattedEndTime && ` - ${formattedEndTime}`}
                                    </span>
                                </div>
                                <div className="flex items-center bg-indigo-50 text-indigo-800 rounded-lg px-4 py-2">
                                    <MapPinned className="h-5 w-5 mr-2 text-indigo-500" />
                                    <span>{event.venue}</span>
                                </div>
                            </div>
                            <div className="flex flex-row gap-3">
                                <div className="flex flex-wrap gap-3 mb-6">
                                    <AddToCalendar event={event} showExpandedCTA={false} />
                                    <ShareButton event={event} showExpandedCTA={false} />
                                    {event.event_url && <EventLink url={event.event_url} />}
                                </div>
                            </div>
                            {event.description && (
                                <div className="mt-6">
                                    <h2 className="text-xl font-semibold text-gray-800 mb-3">About This Event</h2>
                                    <div className="prose prose-indigo max-w-none text-gray-700">
                                        <p>{event.description}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
};

export default EventPage;